'use client';

import { clsx } from 'clsx';
import dayjs, { type Dayjs } from 'dayjs';
import Image from 'next/image';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FaPause, FaPlay, FaRegClock, FaStop } from 'react-icons/fa';
import { TbDecimal } from 'react-icons/tb';

import {
  TaskTimeEntrySelect,
  UpdateTaskStatus,
} from '@/features/tasks/components';
import { useCreateTimeEntryMutation } from '@/features/time-entries/api/timeEntriesApi';
import {
  elapsedSecondsToHours,
  formatClockInput,
  formatElapsedClock,
  parseTimeEntryHours,
} from '@/features/time-entries/time-tracker/utils/timeTrackerTime';
import { useGetCurrentUserContextQuery } from '@/features/users/api/usersApi';
import { useAppSelector } from '@/store/hooks';
import {
  DatePicker,
  DeleteIcon,
  MainButton,
  SecondaryButton,
  SectionTabs,
  SmartInput,
  Tooltip,
} from '@web/ui';
import {
  getErrorMessage,
  getTimeTrackerShortcutLabel,
  notify,
} from '@web/utils';

import activeIcon from './assets/Time-Active-white.svg';
import idleIcon from './assets/Time-Idle-white.svg';
import styles from './TimeTracker.module.scss';
import {
  TIME_TRACKER_OPEN_TASK_EVENT,
  type TimeTrackerOpenTaskDetail,
} from './timeTrackerEvents';
import {
  clearPersistedTimeTracker,
  loadPersistedTimeTracker,
  savePersistedTimeTracker,
  TIME_TRACKER_DISCARD_EVENT,
} from './timeTrackerPersistence';

import type { Task } from '@/features/tasks/types';

type TrackerMode = 'timer' | 'manual';
type TimeFormat = 'clock' | 'decimal';
type TimerStatus = 'idle' | 'running' | 'paused' | 'confirming';
type TimeTrackerViewport = 'desktop' | 'mobile';

type TimeTrackerProps = {
  viewport?: TimeTrackerViewport;
};

const MODE_TABS = [
  { id: 'timer', label: 'Timer' },
  { id: 'manual', label: 'Manual Time' },
];

const QUICK_HOURS = [0.25, 0.5, 1, 2];
const TIME_TRACKER_HEIGHT_CSS_VAR = '--nn-time-tracker-height';
const MOBILE_TIME_TRACKER_SUMMARY_HEIGHT_CSS_VAR =
  '--nn-mobile-time-tracker-summary-height';
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
const TIME_FORMAT_STORAGE_PREFIX = 'netnet:time-tracker-format';

function getIsViewportEnabled(viewport?: TimeTrackerViewport) {
  if (
    !viewport ||
    typeof window === 'undefined' ||
    typeof window.matchMedia === 'undefined'
  ) {
    return true;
  }

  const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  return viewport === 'mobile' ? isMobile : !isMobile;
}

function isEditableShortcutTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest('input, textarea, select, [contenteditable="true"]'),
  );
}

function isTimeTrackerShortcut(event: KeyboardEvent) {
  return (
    event.code === 'Space' &&
    (event.ctrlKey || event.metaKey) &&
    !event.altKey &&
    !event.shiftKey
  );
}

function getBgVar(status: TimerStatus, mode: TrackerMode) {
  if (status === 'confirming') {
    return 'var(--nn-color-primary-glass)';
  }

  if (status === 'running' || mode === 'manual') {
    return 'var(--nn-accent-green-glass-time-tracker)';
  }

  return 'var(--nn-accent-blue-glass-time-tracker)';
}

function formatManualButton(hours: number, format: TimeFormat) {
  return format === 'clock'
    ? formatClockInput(hours).replace(/^0/, '')
    : String(hours);
}

function formatEntryDate(value: Dayjs | null) {
  return (value ?? dayjs()).format('YYYY-MM-DD');
}

function getTimeFormatStorageKey(userId?: string, workspaceId?: string) {
  if (!userId || !workspaceId) {
    return null;
  }

  return `${TIME_FORMAT_STORAGE_PREFIX}:${userId}:${workspaceId}`;
}

function loadPersistedTimeFormat(userId?: string, workspaceId?: string) {
  if (
    typeof window === 'undefined' ||
    typeof window.localStorage === 'undefined'
  ) {
    return null;
  }

  const key = getTimeFormatStorageKey(userId, workspaceId);
  if (!key) {
    return null;
  }

  const value = window.localStorage.getItem(key);
  return value === 'clock' || value === 'decimal' ? value : null;
}

function savePersistedTimeFormat(
  userId: string | undefined,
  workspaceId: string | undefined,
  format: TimeFormat,
) {
  if (
    typeof window === 'undefined' ||
    typeof window.localStorage === 'undefined'
  ) {
    return;
  }

  const key = getTimeFormatStorageKey(userId, workspaceId);
  if (!key) {
    return;
  }

  window.localStorage.setItem(key, format);
}

function formatManualValue(hours: number, format: TimeFormat) {
  return format === 'clock' ? formatClockInput(hours) : String(hours);
}

export function TimeTracker({ viewport }: TimeTrackerProps) {
  const [isViewportEnabled, setIsViewportEnabled] = useState(() =>
    getIsViewportEnabled(viewport),
  );
  const [shortcutLabel] = useState(() => getTimeTrackerShortcutLabel());
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [mode, setMode] = useState<TrackerMode>('timer');
  const [timeFormat, setTimeFormat] = useState<TimeFormat>('clock');
  const [entryDate, setEntryDate] = useState<Dayjs | null>(dayjs());
  const [note, setNote] = useState('');
  const [manualValue, setManualValue] = useState('0:15');
  const [manualValueTouched, setManualValueTouched] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerStatus, setTimerStatus] = useState<TimerStatus>('idle');
  const trackerRef = useRef<HTMLElement | null>(null);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const originalTitleRef = useRef<string | null>(null);
  const restoredStorageKeyRef = useRef<string | null>(null);

  const { data: currentUserContextResponse } = useGetCurrentUserContextQuery();
  const currentWorkspace = useAppSelector((s) => s.workspaces.currentWorkspace);
  const [createTimeEntry, { isLoading: isSaving }] =
    useCreateTimeEntryMutation();

  const currentUserId = currentUserContextResponse?.data?.user?.id;
  const currentWorkspaceId = currentWorkspace?.id;
  const isActive = isOpen || timerStatus !== 'idle';
  const elapsedLabel = formatElapsedClock(elapsedSeconds);
  const canSubmit = Boolean(selectedTask) && !isSaving;
  const timeTrackerButtonLabel = `Time tracker (${shortcutLabel})`;

  const trackerStyle = useMemo(
    () =>
      ({
        '--time-tracker-bg': getBgVar(timerStatus, mode),
      }) as React.CSSProperties,
    [mode, timerStatus],
  );

  useEffect(() => {
    if (
      !viewport ||
      typeof window === 'undefined' ||
      typeof window.matchMedia === 'undefined'
    ) {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const syncViewport = () => {
      setIsViewportEnabled(getIsViewportEnabled(viewport));
    };
    syncViewport();

    mediaQuery.addEventListener('change', syncViewport);

    return () => {
      mediaQuery.removeEventListener('change', syncViewport);
    };
  }, [viewport]);

  useEffect(() => {
    if (!isViewportEnabled || typeof window === 'undefined') {
      return;
    }

    const handleShortcut = (event: KeyboardEvent) => {
      if (
        !isTimeTrackerShortcut(event) ||
        isEditableShortcutTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      setIsOpen((current) => {
        if (!current) {
          setMode('timer');
        }

        return !current;
      });
    };

    window.addEventListener('keydown', handleShortcut);

    return () => {
      window.removeEventListener('keydown', handleShortcut);
    };
  }, [isViewportEnabled]);

  useEffect(() => {
    if (!originalTitleRef.current && typeof document !== 'undefined') {
      originalTitleRef.current = document.title;
    }

    return () => {
      if (originalTitleRef.current && typeof document !== 'undefined') {
        document.title = originalTitleRef.current;
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!isOpen) {
      document.documentElement.style.setProperty(
        TIME_TRACKER_HEIGHT_CSS_VAR,
        '0px',
      );
      return;
    }

    const syncHeight = () => {
      const height = trackerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        TIME_TRACKER_HEIGHT_CSS_VAR,
        `${height}px`,
      );
    };

    syncHeight();

    if (typeof ResizeObserver === 'undefined' || !trackerRef.current) {
      return;
    }

    const observer = new ResizeObserver(syncHeight);
    observer.observe(trackerRef.current);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(
        TIME_TRACKER_HEIGHT_CSS_VAR,
        '0px',
      );
    };
  }, [isOpen, mode, timerStatus]);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const resetSummaryHeight = () => {
      document.documentElement.style.setProperty(
        MOBILE_TIME_TRACKER_SUMMARY_HEIGHT_CSS_VAR,
        '0px',
      );
    };

    if (
      viewport !== 'mobile' ||
      !isViewportEnabled ||
      !selectedTask ||
      timerStatus === 'idle'
    ) {
      resetSummaryHeight();
      return;
    }

    const syncSummaryHeight = () => {
      const height = summaryRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        MOBILE_TIME_TRACKER_SUMMARY_HEIGHT_CSS_VAR,
        `${height}px`,
      );
    };

    syncSummaryHeight();

    if (typeof ResizeObserver === 'undefined' || !summaryRef.current) {
      return resetSummaryHeight;
    }

    const observer = new ResizeObserver(syncSummaryHeight);
    observer.observe(summaryRef.current);

    return () => {
      observer.disconnect();
      resetSummaryHeight();
    };
  }, [isViewportEnabled, selectedTask, timerStatus, viewport]);

  useEffect(() => {
    if (!isViewportEnabled || typeof Worker === 'undefined') {
      return;
    }

    const worker = new Worker(
      new URL('./worker/timeTracker.worker.ts', import.meta.url),
      { type: 'module' },
    );

    worker.onmessage = (
      event: MessageEvent<{ type: 'tick'; elapsedSeconds: number }>,
    ) => {
      if (event.data.type === 'tick') {
        setElapsedSeconds(event.data.elapsedSeconds);
      }
    };

    workerRef.current = worker;

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [isViewportEnabled]);

  const resetTracker = useCallback(
    ({ clearPersistence = true }: { clearPersistence?: boolean } = {}) => {
      workerRef.current?.postMessage({ type: 'reset' });
      setElapsedSeconds(0);
      setTimerStatus('idle');
      if (clearPersistence) {
        clearPersistedTimeTracker(currentUserId, currentWorkspaceId);
      }
    },
    [currentUserId, currentWorkspaceId],
  );

  const resetTrackerInputs = useCallback(() => {
    resetTracker();
    setSelectedTask(null);
    setNote('');
    setManualValue(formatManualValue(0.25, timeFormat));
    setManualValueTouched(false);
    setMode('timer');
    setEntryDate(dayjs());
  }, [resetTracker, timeFormat]);

  useEffect(() => {
    if (!isViewportEnabled || !currentUserId || !currentWorkspaceId) {
      return;
    }

    const storageKey = `${currentUserId}:${currentWorkspaceId}`;
    if (restoredStorageKeyRef.current === storageKey) {
      return;
    }

    restoredStorageKeyRef.current = storageKey;

    const persistedState = loadPersistedTimeTracker(
      currentUserId,
      currentWorkspaceId,
    );
    if (!persistedState) {
      const persistedTimeFormat = loadPersistedTimeFormat(
        currentUserId,
        currentWorkspaceId,
      );
      if (persistedTimeFormat) {
        queueMicrotask(() => {
          setTimeFormat(persistedTimeFormat);
          setManualValue(formatManualValue(0.25, persistedTimeFormat));
          setManualValueTouched(false);
        });
      }
      return;
    }

    const nextElapsedSeconds =
      persistedState.timerStatus === 'running'
        ? persistedState.elapsedSeconds +
          Math.max(
            0,
            Math.floor((Date.now() - persistedState.updatedAt) / 1000),
          )
        : persistedState.elapsedSeconds;

    queueMicrotask(() => {
      setSelectedTask(persistedState.selectedTask);
      setMode(persistedState.mode);
      setTimeFormat(persistedState.timeFormat);
      savePersistedTimeFormat(
        currentUserId,
        currentWorkspaceId,
        persistedState.timeFormat,
      );
      setEntryDate(
        persistedState.entryDate ? dayjs(persistedState.entryDate) : dayjs(),
      );
      setNote(persistedState.note);
      setManualValue(persistedState.manualValue);
      setManualValueTouched(true);
      setElapsedSeconds(nextElapsedSeconds);
      setTimerStatus(persistedState.timerStatus);
      setIsOpen(persistedState.timerStatus !== 'idle');

      if (persistedState.timerStatus === 'running') {
        workerRef.current?.postMessage({
          type: 'start',
          elapsedSeconds: nextElapsedSeconds,
        });
      }
    });
  }, [currentUserId, currentWorkspaceId, isViewportEnabled]);

  useEffect(() => {
    if (!isViewportEnabled || !currentUserId || !currentWorkspaceId) {
      return;
    }

    const storageKey = `${currentUserId}:${currentWorkspaceId}`;
    if (restoredStorageKeyRef.current !== storageKey) {
      return;
    }

    if (timerStatus === 'idle' && (!isOpen || !selectedTask)) {
      clearPersistedTimeTracker(currentUserId, currentWorkspaceId);
      return;
    }

    savePersistedTimeTracker({
      userId: currentUserId,
      workspaceId: currentWorkspaceId,
      selectedTask,
      mode,
      timeFormat,
      entryDate: formatEntryDate(entryDate),
      note,
      manualValue,
      elapsedSeconds,
      timerStatus,
      updatedAt: Date.now(),
    });
  }, [
    currentUserId,
    currentWorkspaceId,
    selectedTask,
    mode,
    timeFormat,
    entryDate,
    note,
    manualValue,
    elapsedSeconds,
    timerStatus,
    isOpen,
    isViewportEnabled,
  ]);

  useEffect(() => {
    if (!isViewportEnabled || typeof window === 'undefined') {
      return;
    }

    const handleDiscard = (event: Event) => {
      const workspaceId = (event as CustomEvent<{ workspaceId?: string }>)
        .detail?.workspaceId;
      if (workspaceId && workspaceId !== currentWorkspaceId) {
        return;
      }

      resetTracker({ clearPersistence: false });
      setIsOpen(false);
      setSelectedTask(null);
      setNote('');
      setManualValue(formatManualValue(0.25, timeFormat));
      setManualValueTouched(false);
      setMode('timer');
      setEntryDate(dayjs());
    };

    window.addEventListener(TIME_TRACKER_DISCARD_EVENT, handleDiscard);

    return () => {
      window.removeEventListener(TIME_TRACKER_DISCARD_EVENT, handleDiscard);
    };
  }, [currentWorkspaceId, isViewportEnabled, resetTracker, timeFormat]);

  useEffect(() => {
    if (!isViewportEnabled || typeof window === 'undefined') {
      return;
    }

    const handleOpenTask = (event: Event) => {
      const { task } =
        (event as CustomEvent<TimeTrackerOpenTaskDetail>).detail ?? {};
      if (!task) {
        return;
      }

      setSelectedTask(task);
      setMode('timer');
      setIsOpen(true);
    };

    window.addEventListener(TIME_TRACKER_OPEN_TASK_EVENT, handleOpenTask);

    return () => {
      window.removeEventListener(TIME_TRACKER_OPEN_TASK_EVENT, handleOpenTask);
    };
  }, [isViewportEnabled]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (timerStatus === 'running' || timerStatus === 'paused') {
      document.title = `${elapsedLabel} • Net Net`;
      return;
    }

    if (originalTitleRef.current) {
      document.title = originalTitleRef.current;
    }
  }, [elapsedLabel, timerStatus]);

  useEffect(() => {
    if (timerStatus !== 'running' || workerRef.current) {
      return;
    }

    const fallbackInterval = setInterval(() => {
      setElapsedSeconds((current) => current + 1);
    }, 1000);

    return () => clearInterval(fallbackInterval);
  }, [timerStatus]);

  const submitTimeEntry = async (hours: number, source: 'manual' | 'timer') => {
    if (!selectedTask) {
      notify({ type: 'error', message: 'Choose a task first.' });
      return false;
    }

    try {
      await createTimeEntry({
        entry_date: formatEntryDate(entryDate),
        hours,
        note: note.trim() || undefined,
        source,
        task_id: selectedTask.id,
      }).unwrap();

      notify({ type: 'success', message: 'Time entry saved.' });
      return true;
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
      return false;
    }
  };

  const handleManualSave = async () => {
    const parsed = parseTimeEntryHours(manualValue);
    if (!parsed.ok) {
      notify({ type: 'error', message: parsed.error });
      return;
    }

    const didSave = await submitTimeEntry(parsed.hours, 'manual');
    if (didSave) {
      resetTrackerInputs();
      setIsOpen(false);
    }
  };

  const handleTimerStart = () => {
    if (!selectedTask) {
      notify({ type: 'error', message: 'Choose a task first.' });
      setIsOpen(true);
      return;
    }

    setTimerStatus('running');
    workerRef.current?.postMessage({ type: 'start', elapsedSeconds });
  };

  const handleTimerPause = () => {
    workerRef.current?.postMessage({ type: 'pause' });
    setTimerStatus('paused');
  };

  const handleTimerStop = () => {
    workerRef.current?.postMessage({ type: 'pause' });
    setTimerStatus('confirming');
    setIsOpen(true);
  };

  const handleTimerConfirm = async () => {
    const hours = elapsedSecondsToHours(elapsedSeconds);
    if (hours <= 0) {
      notify({ type: 'error', message: 'Timer must be greater than zero.' });
      return;
    }

    const didSave = await submitTimeEntry(hours, 'timer');
    if (didSave) {
      resetTrackerInputs();
      setIsOpen(false);
    }
  };

  const adjustTimer = (deltaMinutes: number) => {
    setElapsedSeconds((current) => {
      const nextElapsedSeconds = Math.max(0, current + deltaMinutes * 60);

      if (timerStatus === 'running') {
        workerRef.current?.postMessage({
          type: 'start',
          elapsedSeconds: nextElapsedSeconds,
        });
      }

      return nextElapsedSeconds;
    });
  };

  const handleQuickTime = (hours: number) => {
    setManualValueTouched(true);
    setManualValue((currentValue) => {
      const parsed = parseTimeEntryHours(currentValue);
      const baseHours = manualValueTouched && parsed.ok ? parsed.hours : 0;
      const nextHours = baseHours + hours;
      return formatManualValue(nextHours, timeFormat);
    });
  };

  const handleTimeFormatChange = (nextFormat: TimeFormat) => {
    setTimeFormat(nextFormat);
    savePersistedTimeFormat(currentUserId, currentWorkspaceId, nextFormat);
    setManualValue((currentValue) => {
      const parsed = parseTimeEntryHours(currentValue);
      if (!parsed.ok) {
        return currentValue;
      }

      return formatManualValue(parsed.hours, nextFormat);
    });
  };

  const handleManualValueChange = (nextValue: string) => {
    setManualValueTouched(true);
    setManualValue(nextValue);
  };

  const taskStatusControl = selectedTask ? (
    <div className={styles.timeTracker__status}>
      <UpdateTaskStatus taskId={selectedTask.id} value={selectedTask.status} />
    </div>
  ) : null;

  return (
    <>
      {timerStatus === 'running' ? null : (
        <SecondaryButton
          variant="medium"
          ariaLabel={timeTrackerButtonLabel}
          backgroundOverride={isActive ? 'emphasis' : 'theme'}
          onClick={() => {
            setIsOpen((current) => {
              if (!current) {
                setMode('timer');
              }

              return !current;
            });
          }}
        >
          <Image
            className={clsx(styles.timeTrackerIcon, {
              [styles.timeTrackerIconActive]: isActive,
            })}
            src={isActive ? activeIcon : idleIcon}
            alt=""
            width={14}
            height={14}
          />
        </SecondaryButton>
      )}

      {selectedTask && timerStatus !== 'idle' ? (
        <div
          ref={summaryRef}
          role="button"
          tabIndex={0}
          className={styles.timeTrackerSummary}
          aria-label="Toggle active time tracker"
          onClick={() => setIsOpen((current) => !current)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
              return;
            }

            event.preventDefault();
            setIsOpen((current) => !current);
          }}
        >
          <span className={styles.timeTrackerSummary__content}>
            <Tooltip
              title={selectedTask.title || 'Untitled task'}
              placement="bottom"
            >
              <span className={styles.timeTrackerSummary__title}>
                {selectedTask.title || 'Untitled task'}
              </span>
            </Tooltip>
            <span className={styles.timeTrackerSummary__meta}>
              {timerStatus === 'running'
                ? 'Running'
                : timerStatus === 'paused'
                  ? 'Paused'
                  : 'Stopped'}{' '}
              • {elapsedLabel}
            </span>
          </span>

          <span className={styles.timeTrackerSummary__actions}>
            <button
              type="button"
              className={styles.timeTrackerSummary__action}
              aria-label={
                timerStatus === 'running' ? 'Pause timer' : 'Resume timer'
              }
              onClick={(event) => {
                event.stopPropagation();
                if (timerStatus === 'running') {
                  handleTimerPause();
                } else {
                  handleTimerStart();
                }
              }}
            >
              {timerStatus === 'running' ? <FaPause /> : <FaPlay />}
            </button>

            <button
              type="button"
              className={styles.timeTrackerSummary__action}
              aria-label="Stop timer"
              disabled={timerStatus === 'confirming'}
              onClick={(event) => {
                event.stopPropagation();
                handleTimerStop();
              }}
            >
              <FaStop />
            </button>
          </span>
        </div>
      ) : null}

      {isOpen ? (
        <section
          ref={trackerRef}
          className={clsx(styles.timeTracker, {
            [styles.timeTrackerTimer]: mode === 'timer',
            [styles.timeTrackerManual]: mode === 'manual',
          })}
          style={trackerStyle}
          aria-label="Time tracker"
        >
          <div className={styles.timeTracker__task}>
            <TaskTimeEntrySelect
              selectedTask={selectedTask}
              assigneeId={currentUserId}
              onTaskSelect={setSelectedTask}
            />
          </div>

          <div className={styles.timeTracker__note}>
            <SmartInput
              type="text"
              name="time-entry-note"
              label="Notes..."
              value={note}
              onChange={setNote}
            />
          </div>

          {mode === 'manual' ? (
            <div className={styles.timeTracker__manualControls}>
              <DatePicker
                className={styles.timeTracker__datePicker}
                value={entryDate}
                onChange={setEntryDate}
                size="small"
                fullWidth={false}
              />

              {QUICK_HOURS.map((hours) => (
                <SecondaryButton
                  key={hours}
                  variant="mini"
                  ariaLabel={`Add ${formatManualButton(hours, timeFormat)}`}
                  onClick={() => handleQuickTime(hours)}
                  style={{
                    width: 'auto',
                    padding: '0.8rem 0.3rem',
                    paddingTop: '0.9rem',
                  }}
                >
                  <span>{formatManualButton(hours, timeFormat)}</span>
                </SecondaryButton>
              ))}

              <div className={styles.timeTracker__timeInput}>
                <SmartInput
                  type="text"
                  name="manual-time-entry-hours"
                  value={manualValue}
                  onChange={handleManualValueChange}
                />
              </div>

              <div className={styles.timeTracker__formatButtons}>
                <SecondaryButton
                  variant="mini"
                  ariaLabel="Decimal format"
                  backgroundOverride={
                    timeFormat === 'decimal' ? 'emphasis' : 'theme'
                  }
                  onClick={() => handleTimeFormatChange('decimal')}
                >
                  <TbDecimal />
                </SecondaryButton>

                <SecondaryButton
                  variant="mini"
                  ariaLabel="Clock format"
                  backgroundOverride={
                    timeFormat === 'clock' ? 'emphasis' : 'theme'
                  }
                  onClick={() => handleTimeFormatChange('clock')}
                >
                  <FaRegClock />
                </SecondaryButton>
              </div>

              {taskStatusControl}
            </div>
          ) : null}

          <SectionTabs
            sections={MODE_TABS}
            activeId={mode}
            onSectionChange={(id) => setMode(id as TrackerMode)}
            className={styles.timeTracker__modeTabs}
            aria-label="Time entry mode"
          />

          {mode === 'timer' ? (
            <div className={styles.timeTracker__timerControls}>
              {taskStatusControl}

              {timerStatus === 'idle' ? (
                <MainButton disabled={!canSubmit} onClick={handleTimerStart}>
                  START
                </MainButton>
              ) : null}

              {timerStatus === 'running' || timerStatus === 'paused' ? (
                <>
                  <span className={styles.timeTracker__elapsed}>
                    {elapsedLabel}
                  </span>
                  {[-5, -1, 1, 5].map((delta) => (
                    <SecondaryButton
                      key={delta}
                      variant="medium"
                      ariaLabel={`${
                        delta > 0 ? 'Add' : 'Subtract'
                      } ${Math.abs(delta)} minutes`}
                      onClick={() => adjustTimer(delta)}
                    >
                      <span className={styles.timeTracker__delta}>
                        {delta > 0 ? `+${delta}` : delta}
                      </span>
                    </SecondaryButton>
                  ))}
                  {timerStatus === 'running' ? (
                    <SecondaryButton
                      variant="medium"
                      ariaLabel="Pause timer"
                      onClick={handleTimerPause}
                    >
                      <FaPause />
                    </SecondaryButton>
                  ) : (
                    <SecondaryButton
                      variant="medium"
                      ariaLabel="Resume timer"
                      onClick={handleTimerStart}
                    >
                      <FaPlay />
                    </SecondaryButton>
                  )}
                  <SecondaryButton
                    variant="medium"
                    ariaLabel="Stop timer"
                    onClick={handleTimerStop}
                  >
                    <FaStop />
                  </SecondaryButton>
                </>
              ) : null}

              {timerStatus === 'confirming' ? (
                <>
                  <span className={styles.timeTracker__elapsed}>
                    {elapsedLabel}
                  </span>
                  <span className={styles.timeTracker__stopped}>Stopped</span>
                  <SecondaryButton
                    variant="medium"
                    ariaLabel="Resume timer"
                    onClick={handleTimerStart}
                  >
                    <FaPlay />
                  </SecondaryButton>
                  <MainButton
                    disabled={!canSubmit}
                    isLoading={isSaving}
                    onClick={handleTimerConfirm}
                  >
                    CONFIRM
                  </MainButton>
                  <MainButton
                    variant="error"
                    disabled={isSaving}
                    onClick={() => {
                      resetTracker();
                      setIsOpen(false);
                    }}
                  >
                    DISCARD
                  </MainButton>
                </>
              ) : null}
            </div>
          ) : (
            <div className={styles.timeTracker__primaryAction}>
              <MainButton
                disabled={!canSubmit}
                isLoading={isSaving}
                onClick={handleManualSave}
              >
                SAVE
              </MainButton>
            </div>
          )}

          <div className={styles.timeTracker__closeAction}>
            <SecondaryButton
              variant="medium"
              ariaLabel="Clear time tracker"
              onClick={() => {
                resetTrackerInputs();
                setIsOpen(false);
              }}
            >
              <DeleteIcon />
            </SecondaryButton>
          </div>
        </section>
      ) : null}
    </>
  );
}
